import { useOutletContext } from "react-router-dom";

import PageAbstract from "components/PageAbstract";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import _request from "../../actions/_request";
import i18n from "../../i18";

import _helper from "../../actions/_helper";
import moment from "moment-timezone";
import React from "react";
import { Delete, Edit, Cancel, Replay, ExitToApp } from "@mui/icons-material";

import "../teams/style.css";

const DEFAULT_ROLE = "editor";
const ROLES = ["admin", "editor", "viewer"];
const PERM_KEYS = ["manage-accounts", "create-posts", "delete-posts"];

const emptyDialogForm = () => ({
  role: DEFAULT_ROLE,
  "manage-accounts": false,
  "create-posts": true,
  "delete-posts": false,
});

const parsePermissions = (permissions) => {
  const form = emptyDialogForm();
  const src = permissions || {};
  if (typeof src.role === "string" && ROLES.includes(src.role)) {
    form.role = src.role;
  }
  PERM_KEYS.forEach((k) => {
    form[k] = src[k] === "1" || src[k] === 1 || src[k] === true;
  });
  return form;
};

const buildPermissions = (form) => {
  const out = { role: form.role };
  PERM_KEYS.forEach((k) => {
    out[k] = form[k] ? "1" : "0";
  });
  return out;
};

const enabledPermLabels = (permissions) => {
  const form = parsePermissions(permissions);
  return PERM_KEYS.filter((k) => form[k]).map((k) => i18n.t(`teamsPage.perms.${k}`));
};

class TeamsPage extends PageAbstract {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      userTeam: [],
      selectedUserTeams: [],
      metaData: {},
      tab: "invitees",
      dialogForm: emptyDialogForm(),
    };
  }

  async loadData() {
    const resp = await _request.userTeam.getAll();
    this.state.userTeam = resp?.data?.items || [];

    const res = await _request.users.getMeta();
    const metaData = res?.data?.user || {};

    this.setState({ metaData });
  }

  get myId() {
    return this.state.metaData?.id;
  }

  splitTeams() {
    const { userTeam } = this.state;
    const myId = this.myId;
    const invitees = [];
    const memberships = [];
    userTeam.forEach((item) => {
      if (myId && item.userid === myId && item.ownerid !== myId) {
        memberships.push(item);
      } else {
        invitees.push(item);
      }
    });
    return { invitees, memberships };
  }

  setDialogForm(partial) {
    this.setState({ dialogForm: { ...this.state.dialogForm, ...partial } });
  }

  addUserTeams(id) {
    const name = this.newName?.value;
    const email = this.newEmail?.value;
    const { dialogForm } = this.state;

    const obj = {
      name,
      email,
      permissions: buildPermissions(dialogForm),
    };
    if (id) obj.id = id;

    _request.userTeam.loading(async () => {
      await _request.userTeam.add(obj);
      this.refresh();
    });
  }

  addTeamsShow() {
    this.setState({ dialogForm: emptyDialogForm() }, () => {
      this.openEditFolderDialog(
        i18n.t("teamsPage.invite"),
        this.addUserTeams.bind(this, null),
        {},
        []
      );
    });
  }

  getPermissionFields() {
    const { dialogForm } = this.state;
    return (
      <Box className="flex column gap8">
        <Typography component="span" className="font w700 f16">
          {i18n.t("teamsPage.role")}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <Select
            value={dialogForm.role}
            onChange={(e) => this.setDialogForm({ role: e.target.value })}
            sx={{
              color: "var(--text-color)",
              backgroundColor: "var(--background)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--text-color)",
              },
            }}>
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {i18n.t(`teamsPage.roles.${r}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography
          component="span"
          className="font w700 f16"
          sx={{ mt: 1 }}>
          {i18n.t("teamsPage.permission")}
        </Typography>
        {PERM_KEYS.map((k) => (
          <Box key={k} className="flex items-center gap-8">
            <Checkbox
              color="info"
              checked={dialogForm[k]}
              onChange={(e) => this.setDialogForm({ [k]: e.target.checked })}
            />
            <Typography component="span" className="flex-1">
              {i18n.t(`teamsPage.perms.${k}`)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  getEditFolderContent(tProps, onClick, hideCb) {
    return (
      <Box className="flex column gap16">
        <Typography component={"span"} className="font w700 f16">
          {i18n.t("teamsPage.fullName")}
        </Typography>
        {this.insertGlobalInput(
          {
            inputRef: (el) => (this.newName = el),
            autoFocus: true,
            size: "small",
            sx: { minWidth: "400px" },
            InputProps: {
              sx: {
                color: "var(--text-color)",
                backgroundColor: "var(--background)",
                border: "1px solid var(--text-color)",
                borderRadius: "7px",
                minWidth: "400px",
              },
            },
            ...tProps,
          },
          {
            onEnter: () => {
              hideCb();
              onClick();
            },
          }
        )}
        <Typography component={"span"} className="font w700 f16">
          {i18n.t("teamsPage.email")}
        </Typography>
        {this.insertGlobalInput(
          {
            inputRef: (el) => (this.newEmail = el),
            autoFocus: false,
            size: "small",
            sx: { minWidth: "400px" },
            InputProps: {
              sx: {
                color: "var(--text-color)",
                backgroundColor: "var(--background)",
                border: "1px solid var(--text-color)",
                borderRadius: "7px",
                minWidth: "400px",
              },
            },
            ...tProps,
          },
          {
            onEnter: () => {
              hideCb();
              onClick();
            },
          }
        )}
        {this.getPermissionFields()}
      </Box>
    );
  }

  getEditPermContent() {
    return (
      <Box sx={{ p: 2, minWidth: 300 }}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("teamsPage.permission")}
        </Typography>
        {this.getPermissionFields()}
      </Box>
    );
  }

  openEditFolderDialog(label, onClick, tProps = {}) {
    this.openEditDialog("pageDialogProps", {
      title: "teamsPage.team",
      label,
      onClick,
      text: this.getEditFolderContent.bind(
        this,
        tProps,
        onClick,
        this.changeState.bind(this, "pageDialogProps", null)
      ),
    });
  }

  openEditPermDialog(label, onClick) {
    this.openEditDialog("pageDialogProps", {
      title: "teamsPage.team",
      label,
      onClick,
      text: this.getEditPermContent.bind(this),
    });
  }

  deleteUser({ id: item }) {
    _helper.confirm(
      "dialog.teamRemove",
      this.deleteDraftConfirmed.bind(this, item)
    );
  }

  deleteDraftConfirmed(id) {
    _request.userTeam.loading(async () => {
      await _request.userTeam.delete(id);
      this.refresh();
    });
  }

  editPermissions({ id, permissions }) {
    this.setState({ dialogForm: parsePermissions(permissions) }, () => {
      this.openEditPermDialog(
        i18n.t("teamsPage.edit"),
        this.updateUserTeams.bind(this, id)
      );
    });
  }

  updateUserTeams(id) {
    const { dialogForm } = this.state;
    const obj = {
      permissions: buildPermissions(dialogForm),
    };
    if (id) obj.id = id;

    _request.userTeam.loading(async () => {
      await _request.userTeam.addUpdate(obj);
      this.refresh();
    });
  }

  deleteMultiUser() {
    const { selectedUserTeams } = this.state;

    _helper.confirm(
      "dialog.teamRemove",
      this.deleteDraftConfirmed.bind(this, selectedUserTeams)
    );
  }

  leaveTeam({ id }) {
    _helper.confirm("dialog.teamLeave", () => {
      _request.userTeam.loading(async () => {
        await _request.userTeam.delete(id);
        this.refresh();
      });
    });
  }

  resendInvite(item) {
    const obj = {
      name: item.name,
      email: item.email,
      permissions: item.permissions,
    };
    if (item.id) obj.id = item.id;

    _request.userTeam.loading(async () => {
      await _request.userTeam.add(obj);
      this.refresh();
    });
  }

  handleCheckboxChange(item) {
    const { selectedUserTeams } = this.state;

    const updatedSelection = selectedUserTeams.includes(item.id)
      ? selectedUserTeams.filter((id) => id !== item.id)
      : [...selectedUserTeams, item.id];

    this.setState({
      selectedUserTeams: updatedSelection,
    });
  }

  statusBadge(item) {
    return (
      <Badge
        color="default"
        badgeContent={
          item.status === 1
            ? i18n.t("teamsPage.approved")
            : i18n.t("teamsPage.pending")
        }
        sx={{
          "& .MuiBadge-badge": {
            backgroundColor: item.status === 1 ? "#73E2E0" : "#E2BC73",
            color: "#212832",
          },
        }}
      />
    );
  }

  lastActiveBadge(item) {
    return (
      <Badge
        color="default"
        sx={{
          "width": "auto",
          "whiteSpace": "nowrap",
          "& .MuiBadge-badge": {
            backgroundColor:
              item.last_active_time === null ? "#73E2E09E" : "#73E2E0",
            color: "#212832",
          },
        }}
        badgeContent={
          item.last_active_time === null
            ? i18n.t("teamsPage.notAvailable")
            : moment.utc(item.last_active_time).local().fromNow()
        }
      />
    );
  }

  renderInviteesTable(invitees) {
    const cellStyle = {
      fontWeight: 600,
      fontSize: "16px",
      color: "#a3a6a8",
      border: "none",
      borderBottom: "none",
      padding: "16px 12px",
    };

    const tableBodyStyle = {
      backgroundColor: "var(--background-menu)",
    };

    const tableCellBodyStyle = {
      border: "none",
      borderBottom: "none",
      padding: "16px 12px",
      backgroundColor: "transparent",
    };

    return (
      <Table
        sx={{
          "& .MuiTableHead-root .MuiTableRow-root": {
            backgroundColor: "var(--background-menu)",
          },
          "& .MuiTableCell-root": { border: "none", borderBottom: "none" },
          "& .MuiTableRow-root:hover": {
            backgroundColor: "var(--background-hover, rgba(255, 255, 255, 0.05))",
          },
        }}>
        <TableHead>
          <TableRow
            className="table-head clearfix"
            sx={{ backgroundColor: "var(--background-menu)" }}>
            <TableCell style={{ ...cellStyle, width: "5%", textAlign: "center" }}>
              <Button
                className="btn btn-light"
                onClick={this.deleteMultiUser.bind(this)}>
                <Delete sx={{ color: "var(--text-color)" }} />
              </Button>
            </TableCell>
            <TableCell style={{ ...cellStyle, width: "15%" }}>
              {i18n.t("teamsPage.name")}
            </TableCell>
            <TableCell style={{ ...cellStyle, width: "20%" }}>
              {i18n.t("teamsPage.email")}
            </TableCell>
            <TableCell style={{ ...cellStyle, textAlign: "center", width: "20%" }}>
              {i18n.t("teamsPage.status")}
            </TableCell>
            <TableCell style={{ ...cellStyle, textAlign: "center", width: "20%" }}>
              {i18n.t("teamsPage.lastActive")}
            </TableCell>
            <TableCell style={{ ...cellStyle, textAlign: "center", width: "20%" }}>
              {i18n.t("teamsPage.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody style={{ ...tableBodyStyle }}>
          {invitees
            .sort(
              (a, b) =>
                new Date(b.last_active_time) - new Date(a.last_active_time)
            )
            .map((item) => (
              <TableRow
                key={item.id}
                sx={{
                  "backgroundColor": "var(--background-menu)",
                  "&:hover": {
                    backgroundColor:
                      "var(--background-hover, rgba(255, 255, 255, 0.05))",
                  },
                }}>
                <TableCell style={{ ...tableCellBodyStyle, textAlign: "center" }}>
                  <Checkbox
                    sx={{ color: "var(--text-color)" }}
                    checked={this.state.selectedUserTeams.includes(item.id)}
                    onChange={() => this.handleCheckboxChange(item)}
                  />
                </TableCell>
                <TableCell className="flex" style={tableCellBodyStyle}>
                  <Typography variant="body1" sx={{ color: "var(--text-color)" }}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell className="flex" style={tableCellBodyStyle}>
                  <Typography variant="body1" sx={{ color: "var(--text-color)" }}>
                    {item.email}
                  </Typography>
                </TableCell>
                <TableCell style={{ ...tableCellBodyStyle, textAlign: "center" }}>
                  {this.statusBadge(item)}
                </TableCell>
                <TableCell style={{ ...tableCellBodyStyle, textAlign: "center" }}>
                  {this.lastActiveBadge(item)}
                </TableCell>
                <TableCell style={{ ...tableCellBodyStyle, textAlign: "center" }}>
                  {item.status !== 1 && (
                    <Button
                      className="btn btn-light"
                      title={i18n.t("teamsPage.resendInvitation")}
                      onClick={this.resendInvite.bind(this, item)}>
                      <Replay sx={{ color: "var(--text-color)" }} />
                    </Button>
                  )}
                  <Button
                    className="btn btn-light"
                    title={i18n.t("teamsPage.editPermissions")}
                    onClick={this.editPermissions.bind(this, item)}>
                    <Edit sx={{ color: "var(--text-color)" }} />
                  </Button>
                  <Button
                    className="btn btn-light"
                    title={i18n.t("teamsPage.deleteUser")}
                    onClick={this.deleteUser.bind(this, item)}>
                    <Cancel sx={{ color: "var(--text-color)" }} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    );
  }

  renderPermissionSummary(item) {
    const form = parsePermissions(item.permissions);
    const labels = enabledPermLabels(item.permissions);
    return (
      <Box className="flex gap8 centerV" sx={{ flexWrap: "wrap" }}>
        <Chip
          size="small"
          label={i18n.t(`teamsPage.roles.${form.role}`)}
          sx={{ backgroundColor: "#BAC0FF", color: "#212832", fontWeight: 600 }}
        />
        {labels.map((l) => (
          <Chip
            key={l}
            size="small"
            label={l}
            sx={{ backgroundColor: "#73E2E0", color: "#212832" }}
          />
        ))}
      </Box>
    );
  }

  renderMembershipsTable(memberships) {
    if (!memberships.length) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            color: "var(--text-color)",
            opacity: 0.7,
          }}>
          {i18n.t("teamsPage.noMemberships")}
        </Box>
      );
    }

    const cellStyle = {
      fontWeight: 600,
      fontSize: "16px",
      color: "#a3a6a8",
      border: "none",
      padding: "16px 12px",
    };

    const tableCellBodyStyle = {
      border: "none",
      padding: "16px 12px",
      backgroundColor: "transparent",
    };

    return (
      <Table
        sx={{
          "& .MuiTableHead-root .MuiTableRow-root": {
            backgroundColor: "var(--background-menu)",
          },
          "& .MuiTableCell-root": { border: "none" },
        }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...cellStyle, width: "25%" }}>
              {i18n.t("teamsPage.teamOwner")}
            </TableCell>
            <TableCell style={{ ...cellStyle, width: "20%", textAlign: "center" }}>
              {i18n.t("teamsPage.status")}
            </TableCell>
            <TableCell style={{ ...cellStyle, width: "40%" }}>
              {i18n.t("teamsPage.permission")}
            </TableCell>
            <TableCell style={{ ...cellStyle, width: "15%", textAlign: "center" }}>
              {i18n.t("teamsPage.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ backgroundColor: "var(--background-menu)" }}>
          {memberships.map((item) => (
            <TableRow
              key={item.id}
              sx={{
                "backgroundColor": "var(--background-menu)",
                "&:hover": {
                  backgroundColor:
                    "var(--background-hover, rgba(255, 255, 255, 0.05))",
                },
              }}>
              <TableCell style={tableCellBodyStyle}>
                <Typography variant="body1" sx={{ color: "var(--text-color)" }}>
                  {item.owner_name ||
                    item.owner_email ||
                    `#${item.ownerid || "?"}`}
                </Typography>
              </TableCell>
              <TableCell style={{ ...tableCellBodyStyle, textAlign: "center" }}>
                {this.statusBadge(item)}
              </TableCell>
              <TableCell style={tableCellBodyStyle}>
                {this.renderPermissionSummary(item)}
              </TableCell>
              <TableCell style={{ ...tableCellBodyStyle, textAlign: "center" }}>
                <Button
                  className="btn btn-light"
                  title={i18n.t("teamsPage.leaveTeam")}
                  onClick={this.leaveTeam.bind(this, item)}>
                  <ExitToApp sx={{ color: "var(--text-color)" }} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  renderTopPanel() {
    const { tab } = this.state;
    return (
      <Box className="flex between centerV bgDarkTheme pad10 h-[5.313rem]">
        <Box className="grid gap8 font f28 col black07 p-5">
          <Typography
            className="page-title"
            sx={{ color: "var(--text-color)" }}>
            {i18n.t("teamsPage.title")}
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#6A798E" }}>
            {i18n.t("teamsPage.description")}
          </Typography>
        </Box>
        {tab === "invitees" && (
          <Box className="flex gap8">
            <Box
              className="btnDark"
              onClick={() => this.addTeamsShow()}>
              {i18n.t("teamsPage.add")}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  renderPage() {
    const { tab } = this.state;
    const { invitees, memberships } = this.splitTeams();

    return (
      <Box className="flex1">
        <Box>{this.renderTopPanel()}</Box>
        <Tabs
          value={tab}
          onChange={(_, v) => this.setState({ tab: v })}
          sx={{
            "borderBottom": "1px solid rgba(255,255,255,0.1)",
            "& .MuiTab-root": {
              color: "var(--text-color)",
              opacity: 0.7,
              textTransform: "none",
              fontWeight: 600,
            },
            "& .Mui-selected": { opacity: 1 },
          }}>
          <Tab
            value="invitees"
            label={`${i18n.t("teamsPage.tabs.invitees")} (${invitees.length})`}
          />
          <Tab
            value="memberships"
            label={`${i18n.t("teamsPage.tabs.memberships")} (${memberships.length})`}
          />
        </Tabs>
        {tab === "invitees"
          ? this.renderInviteesTable(invitees)
          : this.renderMembershipsTable(memberships)}
      </Box>
    );
  }
}

export default function Teams() {
  return <TeamsPage {...useOutletContext()} />;
}
